import { format, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ordersApiService from './ordersApiService';
import backendApiService from './backendApiService';
import { API_CONFIG } from '../config/apiConfig';
import localDbService from './localDbService';

class DataService {
  constructor() {
    this.data = [];
    this.filteredData = [];
  }

  // Carrega dados priorizando a API remota (srv-mupa) e mesclando com fontes locais
  async loadData() {
    try {
      let remoteData = [];
      let localData = [];
      let staticData = [];
      
      // 1. Tenta buscar da API remota (srv-mupa)
      console.log('🌐 Tentando buscar ordens recentes da API remota...');
      try {
        const codes = API_CONFIG.SYNC.CUSTOMER_CODES || ['430', '4700'];
        const to = new Date();
        const from = new Date(to.getTime() - (API_CONFIG.SYNC.DATE_RANGE_DAYS || 365) * 24 * 60 * 60 * 1000);
        const date_from = from.toISOString().slice(0, 10);
        const date_to = to.toISOString().slice(0, 10);
        
        const raw = await backendApiService.getCustomersOrdersComplete(codes, { date_from, date_to });
        const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
        
        const ordersOnly = list.filter(r => {
          const t = (r?.type || r?.tipo || '').toString().toLowerCase();
          return !t || t === 'order';
        });
        
        remoteData = ordersOnly.map(o => ordersApiService.mapOrder(o));
        
        console.log(`✅ ${remoteData.length} ordens carregadas via API remota`);

        if (remoteData.length > 0) {
          console.log(`✅ ${remoteData.length} ordens carregadas via API remota`);
          
          // Se estiver em localhost, aproveita e atualiza o banco local em segundo plano
          const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
          if (isLocal) {
            localDbService.importOrders(remoteData).catch(err => {
              console.warn('Falha ao persistir dados da API no banco local:', err.message);
            });
          }
        }
      } catch (apiErr) {
        console.warn('⚠️ Falha ao buscar dados da API remota:', apiErr.message);
      }

      // 2. Sempre tenta carregar o banco local como base sólida
      try {
        console.log('🏠 Buscando ordens do banco de dados local...');
        localData = await localDbService.getOrders({ zaffariOnly: false });
        if (localData.length > 0) {
          console.log(`✅ ${localData.length} ordens carregadas via banco local`);
        }
      } catch (e) {
        console.warn('⚠️ Banco local indisponível:', e.message);
      }

      // 3. Fallback para JSON estático se as fontes acima estiverem vazias
      if (remoteData.length === 0 && localData.length === 0) {
        try {
          const staticPath = API_CONFIG.STATIC_DATA?.ORDERS || '/data/ordens_zaffari.json';
          const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
          const src = `${origin}${staticPath}`;
          
          console.log(`📂 Fallback: Carregando dados estáticos de: ${src}`);
          
          const resp = await fetch(src, { 
            headers: { 'Accept': 'application/json' },
            cache: 'no-store'
          });
          
          if (resp.ok) {
            const json = await resp.json();
            staticData = Array.isArray(json) ? json : (json?.data ?? []);
            console.log(`✅ ${staticData.length} ordens carregadas via JSON estático`);
          }
        } catch (staticErr) {
          console.error('❌ Erro no fallback de JSON estático:', staticErr.message);
        }
      }

      // Mesclar e remover duplicatas (Dando preferência para a API remota que é a mais atual)
      const allData = [...remoteData];
      const remoteIds = new Set(remoteData.map(o => o.ord_ser));
      
      // Adiciona dados locais que não vieram na API
      for (const o of localData) {
        if (!remoteIds.has(o.ord_ser)) {
          allData.push(o);
        }
      }

      // Adiciona dados estáticos se ainda estivermos vazios
      if (allData.length === 0) {
        for (const o of staticData) {
          allData.push(o);
        }
      }

      this.data = this.applyPermissions(allData);
      this.filteredData = [...this.data];

      return this.filteredData;
    } catch (error) {
      console.error('Erro crítico ao carregar dados:', error);
      throw error;
    }
  }

  // Mantido apenas para compatibilidade ou sync forçado, mas a lógica principal agora está no loadData
  async updateFromApi() {
    return this.loadData();
  }

  // Aplica as permissões do usuário logado aos dados
  applyPermissions(data) {
    const userJson = localStorage.getItem('auth_user_profile');
    if (!userJson) {
      // Se não houver perfil salvo, aplica filtro padrão (Zaffari) para segurança
      return data.filter(order => this.isZaffariClient(order.Cliente));
    }

    try {
      const user = JSON.parse(userJson);
      
      // Override para o usuário teste@stok.com: sempre mostra tudo
      const identifier = (user.email || user.username || user.name || '').toLowerCase();
      if (identifier === 'teste@stok.com' || identifier === 'master@siger.com' || identifier === 'antunes@mupa.app') {
        console.log(`🔓 Acesso total concedido (override ${identifier})`);
        return data;
      }

      // Override manual para comercial@zaffari.com
      if (identifier.includes('comercial')) {
        console.log(`🔒 Acesso restrito manual (override ${identifier}): Comercial Zaffari apenas`);
        return data.filter(order => this.isComercialZaffariClient(order.Cliente || order.cliente || ''));
      }

      const allowedCompanies = user.allowedCompanies || [];
      console.log(`User allowedCompanies:`, allowedCompanies);
      console.log(`🕵️ Aplicando permissão por nome de cliente para o usuário: ${identifier}`);

      if (allowedCompanies.includes('Todas')) {
        return data;
      }
      
      const filtered = data.filter(order => {
        const cliente = (order.Cliente || order.cliente || '').toUpperCase();
        
        // Verifica se a ordem deve ser exibida com base nas allowed_companies do usuário
        return allowedCompanies.some(company => {
          const companyStr = String(company).trim();
          
          // Se for o ID 430 -> Comercial Zaffari
          if (companyStr === '430') {
            return cliente.includes('COMERCIAL ZAFFARI');
          }
          
          // Se for o ID 2319 -> Stok Center
          if (companyStr === '2319') {
            return cliente.includes('STOK');
          }
          
          // Se for o ID 2824 -> Companhia Zaffari (Cia Zaffari)
          if (companyStr === '2824') {
            return cliente.includes('COMPANHIA ZAFFARI') || cliente.includes('CIA. ZAFFARI') || cliente.includes('CIA ZAFFARI');
          }

          // Fallback para outros IDs que não sejam os principais de disparo
          // (Pode ser mantida a lógica de ID exato se necessário, mas o foco agora é no nome)
          const customerId = (order.service_order_customer_id || order.CodigoCliente || order.codigo_cliente || '').toString();
          return customerId === companyStr;
        });
      });

      if (filtered.length === 0 && data.length > 0) {
        console.warn(`⚠️ O filtro de permissões retornou 0 ordens para o usuário: ${identifier}.`, {
          allowedCompanies,
          exemploOrdem: {
            cliente: data[0].Cliente,
            service_order_customer_id: data[0].service_order_customer_id
          }
        });
      }

      console.log(`📊 Filtro por nome: ${filtered.length} de ${data.length} ordens mantidas.`);
      return filtered;
    } catch (e) {
      return data.filter(order => this.isZaffariClient(order.Cliente));
    }
  }

  // Verifica se é cliente Zaffari
  isZaffariClient(cliente) {
    if (!cliente) return false;
    const clienteUpper = cliente.toUpperCase();
    // Exclui Stok Center explicitamente se estiver no nome
    if (clienteUpper.includes('STOK')) return false;
    return clienteUpper.includes('ZAFFARI') || clienteUpper.includes('COMERCIAL ZAFFARI');
  }

  // Verifica se é cliente Comercial Zaffari apenas
  isComercialZaffariClient(cliente) {
    if (!cliente) return false;
    const clienteUpper = cliente.toUpperCase();
    return clienteUpper.includes('COMERCIAL ZAFFARI');
  }

  // Verifica se é cliente Stok Center
  isStokCenterClient(cliente) {
    if (!cliente) return false;
    const clienteUpper = cliente.toUpperCase();
    return clienteUpper.includes('STOK') || clienteUpper.includes('STOK CENTER');
  }

  // Converte timestamp Unix para data formatada
  formatDate(timestamp) {
    if (!timestamp) return 'Não informado';
    
    try {
      const date = new Date(timestamp);
      return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : 'Data inválida';
    } catch (error) {
      return 'Data inválida';
    }
  }

  // Formatar valores monetários
  formatCurrency(value) {
    if (!value || value === null) return 'R$ 0,00';
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'R$ 0,00';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue);
  }

  // Trata valores nulos ou undefined
  safeValue(value, defaultValue = '-') {
    return value && value !== null ? value : defaultValue;
  }

  // Obtém estatísticas para o dashboard
  getStats() {
    const normalizeStatus = (s) => {
      if (s === null || s === undefined) return 'Não informado';
      const str = typeof s === 'string' ? s : String(s);
      const map = {
        '0': 'Aguardando diagnóstico',
        '1': 'Orçada/aguardando aprovação',
        '2': 'Aguardando início da execução',
        '3': 'Em execução',
        '4': 'Parada/aguardando material',
        '5': 'Concluída',
        '6': 'Entregue',
        '7': 'Orçamento não aprovado',
        '9': 'Cancelada'
      };
      return map[str] || str;
    };
    const stats = {
      totalOrdens: this.filteredData.length,
      ordensConcluidas: this.filteredData.filter(order => {
        const st = normalizeStatus(order['Posição']);
        return st && st.includes('Concluída');
      }).length,
      totalValorServico: this.filteredData.reduce((sum, order) => {
        const valor = parseFloat(order['Vlr.cobrado ser']) || 0;
        return sum + valor;
      }, 0),
      totalValorMaterial: this.filteredData.reduce((sum, order) => {
        const valor = parseFloat(order['Vlr.cobrado mat']) || 0;
        return sum + valor;
      }, 0)
    };

    return stats;
  }

  // Dados para gráfico de status
  getStatusStats() {
    const normalizeStatus = (s) => {
      if (s === null || s === undefined) return 'Não informado';
      const str = typeof s === 'string' ? s : String(s);
      const map = {
        '0': 'Aguardando diagnóstico',
        '1': 'Orçada/aguardando aprovação',
        '2': 'Aguardando início da execução',
        '3': 'Em execução',
        '4': 'Parada/aguardando material',
        '5': 'Concluída',
        '6': 'Entregue',
        '7': 'Orçamento não aprovado',
        '9': 'Cancelada'
      };
      return map[str] || str;
    };
    const statusCount = {};
    
    this.filteredData.forEach(order => {
      const status = normalizeStatus(this.safeValue(order['Posição'], 'Não informado'));
      statusCount[status] = (statusCount[status] || 0) + 1;
    });

    return Object.entries(statusCount).map(([status, count]) => ({
      label: status,
      value: count
    }));
  }

  // Dados para gráfico de faturamento
  getFaturamentoStats() {
    const faturadas = this.filteredData.filter(order => 
      order['Faturada'] && !order['Faturada'].includes('Não faturada')
    ).length;
    
    const naoFaturadas = this.filteredData.length - faturadas;

    return [
      { label: 'Faturadas', value: faturadas },
      { label: 'Não Faturadas', value: naoFaturadas }
    ];
  }

  // Dados para gráfico de tipo de serviço
  getTipoServicoStats() {
    const tipoCount = {};
    
    this.filteredData.forEach(order => {
      const tipo = this.safeValue(order['Tipo de Ord.Serviço'], 'Não informado');
      tipoCount[tipo] = (tipoCount[tipo] || 0) + 1;
    });

    return Object.entries(tipoCount).map(([tipo, count]) => ({
      label: tipo,
      value: count
    }));
  }

  // Timeline de ordens ao longo do tempo
  getTimelineStats() {
    const monthlyCount = {};
    
    this.filteredData.forEach(order => {
      if (order['Data Inclusao']) {
        try {
          const date = new Date(order['Data Inclusao']);
          if (isValid(date)) {
            const monthKey = format(date, 'yyyy-MM');
            monthlyCount[monthKey] = (monthlyCount[monthKey] || 0) + 1;
          }
        } catch (error) {
          // Ignora datas inválidas
        }
      }
    });

    return Object.entries(monthlyCount)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, count]) => ({
        month,
        label: format(new Date(month + '-01'), 'MMM/yyyy', { locale: ptBR }),
        value: count
      }));
  }

  // Filtrar dados
  filterData(filters) {
    let filtered = this.data;

    // Filtro por status
    if (filters.status && filters.status !== '') {
      filtered = filtered.filter(order => 
        order['Posição'] && order['Posição'].includes(filters.status)
      );
    }

    // Filtro por faturamento
    if (filters.faturada && filters.faturada !== '') {
      if (filters.faturada === 'sim') {
        filtered = filtered.filter(order => 
          order['Faturada'] && !order['Faturada'].includes('Não faturada')
        );
      } else if (filters.faturada === 'nao') {
        filtered = filtered.filter(order => 
          !order['Faturada'] || order['Faturada'].includes('Não faturada')
        );
      }
    }

    // Filtro por problema (busca por palavras-chave)
    if (filters.problema && filters.problema.trim() !== '') {
      const searchTerm = filters.problema.toLowerCase();
      filtered = filtered.filter(order => {
        const problema = (order['Problema'] || '').toLowerCase();
        const descricao = (order['Descrição'] || '').toLowerCase();
        const descProblema = (order['Desc.Problema'] || '').toLowerCase();
        
        return problema.includes(searchTerm) || 
               descricao.includes(searchTerm) || 
               descProblema.includes(searchTerm);
      });
    }

    // Filtro por técnico/atendente
    if (filters.tecnico && filters.tecnico.trim() !== '') {
      const searchTerm = filters.tecnico.toLowerCase();
      filtered = filtered.filter(order => {
        const tecnico = (order['Técnico'] || '').toLowerCase();
        const atendente = (order['Atendente'] || '').toLowerCase();
        
        return tecnico.includes(searchTerm) || atendente.includes(searchTerm);
      });
    }

    // Filtro por intervalo de datas
    if (filters.dataInicio) {
      const dataInicio = new Date(filters.dataInicio);
      filtered = filtered.filter(order => {
        if (!order['Data Inclusao']) return false;
        const orderDate = new Date(order['Data Inclusao']);
        return isValid(orderDate) && orderDate >= dataInicio;
      });
    }

    if (filters.dataFim) {
      const dataFim = new Date(filters.dataFim);
      dataFim.setHours(23, 59, 59, 999); // Inclui o dia inteiro
      filtered = filtered.filter(order => {
        if (!order['Data Inclusao']) return false;
        const orderDate = new Date(order['Data Inclusao']);
        return isValid(orderDate) && orderDate <= dataFim;
      });
    }

    // Busca rápida
    if (filters.busca && filters.busca.trim() !== '') {
      const searchTerm = filters.busca.toLowerCase();
      filtered = filtered.filter(order => {
        const searchFields = [
          order['Ord.Ser']?.toString(),
          order['Descrição'],
          order['Problema'],
          order['Técnico'],
          order['Atendente'],
          order['Cliente']
        ];
        
        return searchFields.some(field => 
          field && field.toString().toLowerCase().includes(searchTerm)
        );
      });
    }

    this.filteredData = filtered;
    return filtered;
  }

  // Obtém dados filtrados atuais
  getFilteredData() {
    return this.filteredData;
  }

  // Obtém todos os dados
  getAllData() {
    return this.data;
  }

  // Obter valores únicos para filtros
  getUniqueValues(field) {
    const values = new Set();
    this.data.forEach(order => {
      const value = order[field];
      if (value && value !== null) {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  }

  // Reset dos filtros
  resetFilters() {
    this.filteredData = this.data;
    return this.filteredData;
  }
}

// Exportar instância singleton
const dataServiceInstance = new DataService();
export default dataServiceInstance;
